/**
 * Resolve the backend API base URL. Resolution order (mirrors BE
 * `AppConfig.backendPublicUrl`):
 *
 *   1. NEXT_PUBLIC_API_URL env var (inlined at build time)
 *      → staging + prod + any dev with explicit .env.local override
 *   2. Production build with no env → THROW (force explicit config)
 *   3. Dev runtime in browser → derive from window.location.port + 1
 *      (matches the FE-port + 1 = BE-port convention: 4000→4001, 4110→4111)
 *   4. SSR-dev edge case (no window, no env) → fall back to :4001 + warn.
 *      This only fires during `next dev` SSR without .env.local — rare.
 *
 * 2026-05-14: prior `process.env.NEXT_PUBLIC_API_URL || 'http://localhost:4001'`
 * fallback silently routed worktree FEs (on a non-4001 BE port) to the
 * main BE port whenever .env.local was missing. The derived-from-window
 * path makes worktree dev self-healing.
 */
function resolveApiBaseUrl(): string {
  const fromEnv = process.env.NEXT_PUBLIC_API_URL;
  if (fromEnv && fromEnv.trim().length > 0) {
    return fromEnv.replace(/\/$/, '');
  }
  if (process.env.NODE_ENV === 'production') {
    throw new Error(
      'NEXT_PUBLIC_API_URL is required in production builds. Set it to the public URL of the backend (e.g. https://hfv3-backend.humanfirewall.me).',
    );
  }
  // Dev browser: derive from FE port + 1 (worktree convention).
  if (typeof window !== 'undefined') {
    const fePort = Number(window.location.port) || 4000;
    return `${window.location.protocol}//${window.location.hostname}:${fePort + 1}`;
  }
  // Dev SSR last-resort — only used if .env.local is absent AND a server
  // component runs at module load. Not reachable in production.
  if (typeof console !== 'undefined') {
    // eslint-disable-next-line no-console
    console.warn(
      '[api/config] NEXT_PUBLIC_API_URL unset and no window — falling back to http://localhost:4001. Set NEXT_PUBLIC_API_URL in .env.local to silence.',
    );
  }
  return 'http://localhost:4001';
}

export const API_CONFIG = {
  // Real backend base URL — see resolveApiBaseUrl above for the 4-tier
  // resolution order. Never reference a hardcoded host/port here.
  baseUrl: resolveApiBaseUrl(),
  // API version prefix for real backend
  versionPrefix: '/v1',
} as const;

// ── Session token storage ─────────────────────────────────────────────────────
// Production is the only mode. Token + workspace live in these keys, written by
// auth-store.login() + workspace-store.setActiveWorkspace().

const REAL_TOKEN_KEY = 'hf-app:access-token';
const REAL_REFRESH_KEY = 'hf-app:refresh-token';
const REAL_WORKSPACE_KEY = 'hf-app:workspace-id';

export const SESSION_KEYS = {
  REAL_TOKEN_KEY,
  REAL_REFRESH_KEY,
  REAL_WORKSPACE_KEY,
} as const;

/** Access-token storage key. (Kept as a function so SSE/upload callers that
 *  build the EventSource URL don't inline the literal.) */
export function tokenKeyFor(): string {
  return REAL_TOKEN_KEY;
}

/** Workspace-id storage key. */
export function workspaceKeyFor(): string {
  return REAL_WORKSPACE_KEY;
}

/**
 * Resolve a backend-hosted file URL (e.g. /uploads/...) to an absolute URL.
 * Returns the original URL if it's already absolute or not a backend path.
 */
export function resolveBackendFileUrl(path: string | null | undefined): string | null {
  if (!path) return null;
  if (path.startsWith('/uploads')) {
    return `${API_CONFIG.baseUrl}${path}`;
  }
  return path;
}
